import { useRef, useState } from "react";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, resolveUrl } from "@/lib/api";

// label, value (stored url or null), fallback (preview when value is null),
// onUploaded(url) called with the new stored url, compact for gallery add tile.
export default function ImageDrop({ label, value, fallback, onUploaded, testid, className = "", aspect = "aspect-video" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const preview = value ? resolveUrl(value) : fallback || null;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setBusy(true);
    try {
      const { url } = await uploadImage(file);
      onUploaded(url);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      {label && (
        <span className="block font-mono text-[10px] tracking-[0.2em] text-cream/50 uppercase mb-2">
          {label}
        </span>
      )}
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={`group relative w-full ${aspect} overflow-hidden border transition-colors duration-300 ${
          drag ? "border-gold bg-gold/10" : "border-white/15 hover:border-gold/50"
        }`}
      >
        {preview ? (
          <img src={preview} alt={label || "preview"} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-panel" />
        )}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center gap-1.5 transition-opacity duration-300 ${
            preview ? "bg-ink/60 opacity-0 group-hover:opacity-100" : "opacity-100"
          }`}
        >
          {busy ? (
            <Loader2 className="text-gold animate-spin" size={22} />
          ) : (
            <>
              <UploadCloud className="text-gold" size={22} />
              <span className="font-mono text-[10px] tracking-[0.15em] text-cream/80 uppercase">
                Drop or click to upload
              </span>
            </>
          )}
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
